// Purchase Order AI Scan — review modal (docs/PO_SCAN_MODULE.md). Uploads a customer's incoming
// PO, lets the AI extraction come back (customer + line items matched against the real catalog),
// then shows an editable review — same shape as the manual "New Order" flow in AdminOrdersPage.js
// — before batch-creating one real CustomerOrder on confirm.
import React, { useState, useEffect, useRef } from 'react';
import poScanService from '../services/poScanService';
import customerOrderService from '../services/customerOrderService';
import SearchableSelect from './SearchableSelect';

const today = () => new Date().toISOString().split('T')[0];

// unitPriceSnapshot must always hold the price for one of whatever quantityUnit represents (case
// or individual unit) — mirrors AdminOrdersPage.js's priceForQuantityUnit() exactly.
const priceForQuantityUnit = (pricePerCase, unitsPerCase, unit) => {
  const ppc = Number(pricePerCase);
  if (!(ppc > 0)) return null;
  if (unit === 'units') {
    const upc = Number(unitsPerCase);
    return upc > 0 ? Math.round((ppc / upc) * 10000) / 10000 : ppc;
  }
  return ppc;
};

function ProductLinePicker({ products, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef();

  const selected = products.find(p => String(p.id) === String(value));

  useEffect(() => {
    setQuery(selected ? selected.name : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery(selected ? selected.name : '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selected]);

  const filtered = query.trim()
    ? products.filter(p =>
        p.name.toLowerCase().includes(query.trim().toLowerCase()) ||
        (p.sku && p.sku.toLowerCase().includes(query.trim().toLowerCase()))
      ).slice(0, 30)
    : products.slice(0, 30);

  return (
    <div ref={containerRef} className="position-relative">
      <input
        className="form-control form-control-sm"
        placeholder="Search product…"
        value={open ? query : (selected ? selected.name : '')}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => setQuery(e.target.value)}
        autoComplete="off"
      />
      {open && (
        <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1060, maxHeight: 200, overflowY: 'auto' }}>
          {value && (
            <li className="list-group-item list-group-item-action py-1 small text-muted"
              onMouseDown={() => { onChange(''); setOpen(false); }}>
              — Clear —
            </li>
          )}
          {filtered.map(p => (
            <li key={p.id} className="list-group-item list-group-item-action py-1 small"
              onMouseDown={() => { onChange(String(p.id)); setOpen(false); }}>
              {p.name}{p.sku ? ` (${p.sku})` : ''}
            </li>
          ))}
          {filtered.length === 0 && <li className="list-group-item py-1 small text-muted">No matching product.</li>}
        </ul>
      )}
    </div>
  );
}

function PriceDiffBadge({ poPrice, systemPrice }) {
  if (poPrice == null || systemPrice == null || !(systemPrice > 0)) return null;
  const pct = ((poPrice - systemPrice) / systemPrice) * 100;
  const rounded = Math.round(pct * 10) / 10;
  if (Math.abs(rounded) < 0.5) return <span className="badge bg-secondary">matches your price</span>;
  const cls = rounded > 0 ? 'bg-warning text-dark' : 'bg-info text-dark';
  const sign = rounded > 0 ? '+' : '';
  return <span className={`badge ${cls}`}>PO {sign}{rounded}% vs your price</span>;
}

export default function PoScanReviewModal({ file, customers, products, onClose, onConfirmed }) {
  const [phase, setPhase] = useState('uploading'); // uploading | scanning | review | confirming | done | error
  const [error, setError] = useState('');
  const [scanId, setScanId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null); // { orderId, orderNumber }

  const [customerId, setCustomerId] = useState('');
  const [requestedDate, setRequestedDate] = useState(today());
  const [poNumber, setPoNumber] = useState('');
  const [orderChannel, setOrderChannel] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [priceList, setPriceList] = useState(null);
  const [lines, setLines] = useState([]);
  const startedRef = useRef(false);
  const confirmedRef = useRef(false);
  const manualKeyRef = useRef(1000000);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPriceListToLines = (pl) => {
    setLines(prev => prev.map(l => {
      if (!l.productId) return l;
      const found = pl?.Items?.find(pi => String(pi.productId) === String(l.productId));
      if (!found) return { ...l, priceListItemId: '' };
      return {
        ...l,
        priceListItemId: String(found.id),
        presentationLabel: found.presentationLabel,
        unitsPerCase: found.unitsPerCase || '',
        pricePerCase: found.pricePerCase || '',
        unitPriceSnapshot: priceForQuantityUnit(found.pricePerCase, found.unitsPerCase, l.quantityUnit) ?? l.unitPriceSnapshot,
      };
    }));
  };

  const run = async () => {
    try {
      setPhase('uploading');
      const { uploadUrl, key } = await poScanService.getUploadUrl(file.name, file.type);
      await poScanService.uploadToS3(uploadUrl, file);

      setPhase('scanning');
      const result = await poScanService.scanPo({ documentKey: key, documentOriginalName: file.name, contentType: file.type });

      setScanId(result.id);
      setMeta({
        extractedCustomerName: result.extractedCustomerName,
        extractedPoNumber: result.extractedPoNumber,
        extractedOrderTotal: result.extractedOrderTotal,
        extractionNotes: result.extractionNotes,
      });
      setPoNumber(result.extractedPoNumber || '');
      setRequestedDate(result.extractedRequestedDate || today());

      const initialLines = (result.lineItems || []).map((li, idx) => ({
        key: idx,
        include: true,
        description: li.description,
        matchConfidence: li.matchConfidence,
        productId: li.matchedProductId ? String(li.matchedProductId) : '',
        priceListItemId: '',
        presentationLabel: '',
        unitsPerCase: '',
        pricePerCase: '',
        unitPriceSnapshot: li.unitPrice != null ? String(li.unitPrice) : '',
        quantity: li.quantity != null ? String(li.quantity) : '',
        quantityUnit: li.quantityUnit === 'units' ? 'units' : 'cases',
        extractedUnitPrice: li.unitPrice != null ? Number(li.unitPrice) : null,
        extractedLineTotal: li.lineTotal != null ? Number(li.lineTotal) : null,
        isManual: false,
      }));
      setLines(initialLines);

      const suggestedId = result.suggestedCustomerId ? String(result.suggestedCustomerId) : '';
      setCustomerId(suggestedId);
      const customer = customers.find(c => String(c.id) === suggestedId);
      if (customer?.defaultOrderChannel) setOrderChannel(customer.defaultOrderChannel);

      if (suggestedId) {
        try {
          const pl = await customerOrderService.getPriceListByCustomer(suggestedId);
          setPriceList(pl);
          setLines(prev => prev.map(l => {
            if (!l.productId) return l;
            const found = pl?.Items?.find(pi => String(pi.productId) === String(l.productId));
            if (!found) return l;
            return {
              ...l,
              priceListItemId: String(found.id),
              presentationLabel: found.presentationLabel,
              unitsPerCase: found.unitsPerCase || '',
              pricePerCase: found.pricePerCase || '',
              unitPriceSnapshot: priceForQuantityUnit(found.pricePerCase, found.unitsPerCase, l.quantityUnit) ?? l.unitPriceSnapshot,
            };
          }));
        } catch {
          setPriceList(null);
        }
      }

      setPhase('review');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong reading this purchase order.');
      setPhase('error');
    }
  };

  const handleCustomerChange = async (newCustomerId) => {
    setCustomerId(newCustomerId);
    const customer = customers.find(c => String(c.id) === String(newCustomerId));
    if (customer?.defaultOrderChannel) setOrderChannel(customer.defaultOrderChannel);
    if (!newCustomerId) { setPriceList(null); return; }
    try {
      const pl = await customerOrderService.getPriceListByCustomer(newCustomerId);
      setPriceList(pl);
      applyPriceListToLines(pl);
    } catch {
      setPriceList(null);
      applyPriceListToLines(null);
    }
  };

  const setLine = (key, field, value) => {
    setLines(prev => prev.map(l => (l.key === key ? { ...l, [field]: value } : l)));
  };

  // Switching cases/units rescales the price snapshot — mirrors AdminOrdersPage.js's setQuantityUnit.
  const setLineQuantityUnit = (key, unit) => {
    setLines(prev => prev.map(l => {
      if (l.key !== key) return l;
      const unitPriceSnapshot = l.pricePerCase
        ? (priceForQuantityUnit(l.pricePerCase, l.unitsPerCase, unit) ?? l.unitPriceSnapshot)
        : l.unitPriceSnapshot;
      return { ...l, quantityUnit: unit, unitPriceSnapshot };
    }));
  };

  const setLinePriceListItem = (key, priceListItemId) => {
    const found = priceList?.Items?.find(i => String(i.id) === String(priceListItemId));
    setLines(prev => prev.map(l => {
      if (l.key !== key) return l;
      if (!found) return { ...l, priceListItemId: '' };
      return {
        ...l,
        priceListItemId: String(found.id),
        productId: found.productId ? String(found.productId) : '',
        presentationLabel: found.presentationLabel,
        unitsPerCase: found.unitsPerCase || '',
        pricePerCase: found.pricePerCase || '',
        unitPriceSnapshot: priceForQuantityUnit(found.pricePerCase, found.unitsPerCase, l.quantityUnit) ?? '',
      };
    }));
  };

  const setLineProduct = (key, productId) => {
    const product = products.find(p => String(p.id) === String(productId));
    setLines(prev => prev.map(l => (l.key === key ? {
      ...l, productId, presentationLabel: product?.name || l.presentationLabel,
    } : l)));
  };

  const addManualLine = () => {
    const key = manualKeyRef.current++;
    setLines(prev => [...prev, {
      key,
      include: true,
      isManual: true,
      description: '',
      matchConfidence: null,
      productId: '',
      priceListItemId: '',
      presentationLabel: '',
      unitsPerCase: '',
      pricePerCase: '',
      unitPriceSnapshot: '',
      quantity: '',
      quantityUnit: 'cases',
      extractedUnitPrice: null,
      extractedLineTotal: null,
    }]);
  };

  const removeManualLine = (key) => setLines(prev => prev.filter(l => l.key !== key));

  const handleClose = () => {
    if (confirmedRef.current) {
      onConfirmed();
    } else if (scanId) {
      poScanService.discardScan(scanId).catch(() => {});
    }
    onClose();
  };

  const validate = () => {
    if (!customerId) return 'Select a customer.';
    if (!requestedDate) return 'Requested date is required.';
    const active = lines.filter(l => l.include);
    if (active.length === 0) return 'At least one line item must be included.';
    for (const l of active) {
      const label = l.description || l.presentationLabel || 'the manually added line';
      if (!l.productId && !l.priceListItemId) return `Select a product for "${label}".`;
      if (!l.quantity || Number(l.quantity) <= 0) return `Enter a valid quantity for "${label}".`;
    }
    return null;
  };

  const handleConfirm = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setPhase('confirming');
    try {
      const payload = {
        customerId: Number(customerId),
        requestedDate,
        orderChannel: orderChannel || null,
        poNumber: poNumber || null,
        customerNotes: customerNotes || null,
        items: lines.filter(l => l.include).map(l => ({
          priceListItemId: l.priceListItemId ? Number(l.priceListItemId) : null,
          productId: l.productId ? Number(l.productId) : null,
          presentationLabel: l.presentationLabel || l.description || null,
          unitsPerCase: l.unitsPerCase ? Number(l.unitsPerCase) : null,
          unitPriceSnapshot: l.unitPriceSnapshot !== '' ? Number(l.unitPriceSnapshot) : null,
          quantity: Number(l.quantity),
          quantityUnit: l.quantityUnit || 'cases',
        })),
      };
      const result = await poScanService.confirmScan(scanId, payload);
      confirmedRef.current = true;
      setConfirmResult(result);
      setPhase('done');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to create the order.');
      setPhase('review');
    }
  };

  const lineTotalsSum = lines
    .filter(l => l.include)
    .reduce((sum, l) => {
      const qty = parseFloat(l.quantity);
      const price = parseFloat(l.unitPriceSnapshot);
      return sum + (qty && price ? qty * price : 0);
    }, 0);

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">📷 Scan Purchase Order</h5>
            <button type="button" className="btn-close" onClick={handleClose} disabled={phase === 'confirming'} />
          </div>
          <div className="modal-body">
            {(phase === 'uploading' || phase === 'scanning') && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" />
                <div className="text-muted">{phase === 'uploading' ? 'Uploading purchase order…' : 'Reading line items with AI…'}</div>
              </div>
            )}

            {phase === 'error' && (
              <div className="alert alert-danger">{error}</div>
            )}

            {(phase === 'review' || phase === 'confirming') && (
              <>
                {error && <div className="alert alert-danger py-2">{error}</div>}
                {meta?.extractionNotes && (
                  <div className="alert alert-warning py-2 small">{meta.extractionNotes}</div>
                )}

                <div className="row g-3 mb-3">
                  <div className="col-md-5">
                    <label className="form-label">Customer <span className="text-danger">*</span></label>
                    <SearchableSelect
                      options={customers.map(c => ({ value: c.id, label: c.name }))}
                      value={customerId}
                      onChange={handleCustomerChange}
                      placeholder="Select customer…"
                    />
                    {meta?.extractedCustomerName && (
                      <div className="form-text">Extracted from PO: <strong>{meta.extractedCustomerName}</strong></div>
                    )}
                    {customerId && !priceList && (
                      <div className="form-text text-warning">No active price list for this customer — enter prices manually below.</div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Requested Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={requestedDate} onChange={e => setRequestedDate(e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">PO Number</label>
                    <input type="text" className="form-control" value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="optional" />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Channel</label>
                    <div className="btn-group d-flex" role="group">
                      {['retail', 'food_service'].map(ch => (
                        <button
                          key={ch}
                          type="button"
                          className={`btn btn-sm ${orderChannel === ch ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setOrderChannel(orderChannel === ch ? '' : ch)}
                        >
                          {ch === 'retail' ? 'Retail' : 'Food Service'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Customer Notes</label>
                    <input type="text" className="form-control" value={customerNotes} onChange={e => setCustomerNotes(e.target.value)} placeholder="optional" />
                  </div>
                </div>

                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Line / Product</th>
                      <th style={{ width: 160 }}>Quantity</th>
                      <th style={{ width: 130 }}>
                        Unit Price <span className="text-muted" style={{ cursor: 'help' }} title="Per case or per unit, matching the Quantity toggle">ⓘ</span>
                      </th>
                      <th style={{ width: 170 }}>vs. Your Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map(line => (
                      <tr key={line.key} className={!line.include ? 'table-warning' : ''}>
                        <td className="align-top py-2">
                          <div className="mb-1" style={{ minHeight: 14, fontSize: 10 }}>
                            {line.isManual ? (
                              <span className="text-muted">✎ Manually added</span>
                            ) : (
                              <>
                                <span className="text-muted">PO says: </span>
                                <code style={{ fontSize: 11, background: '#f8f9fa', padding: '1px 5px', borderRadius: 3 }}>
                                  {line.description}
                                </code>
                              </>
                            )}
                          </div>
                          {priceList ? (
                            <SearchableSelect
                              size="sm"
                              options={(priceList.Items || []).map(i => ({ value: i.id, label: i.presentationLabel }))}
                              value={line.priceListItemId}
                              onChange={v => setLinePriceListItem(line.key, v)}
                              placeholder="Select SKU…"
                            />
                          ) : (
                            <ProductLinePicker products={products} value={line.productId} onChange={v => setLineProduct(line.key, v)} />
                          )}
                          {line.matchConfidence && line.matchConfidence !== 'high' && (line.productId || line.priceListItemId) && (
                            <div className="form-text text-warning">⚠ Low-confidence match — please verify</div>
                          )}
                          <div className="form-check mt-1">
                            <input type="checkbox" className="form-check-input" id={`inc-${line.key}`}
                              checked={line.include} onChange={e => setLine(line.key, 'include', e.target.checked)} />
                            <label className="form-check-label" htmlFor={`inc-${line.key}`} style={{ fontSize: 11 }}>
                              Include in order
                            </label>
                          </div>
                          {line.isManual && (
                            <button type="button" className="btn btn-link btn-sm text-danger p-0"
                              style={{ fontSize: 11, textDecoration: 'none' }}
                              onClick={() => removeManualLine(line.key)}>
                              remove this line
                            </button>
                          )}
                        </td>
                        <td className="align-top py-2" style={{ opacity: line.include ? 1 : 0.4 }}>
                          <div className="mb-1" style={{ minHeight: 14 }}>&nbsp;</div>
                          <div className="input-group input-group-sm">
                            <input type="number" className="form-control" style={{ minWidth: 60 }} value={line.quantity}
                              onChange={e => setLine(line.key, 'quantity', e.target.value)} />
                            <button type="button"
                              className={`btn btn-sm ${line.quantityUnit === 'cases' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                              onClick={() => setLineQuantityUnit(line.key, 'cases')}>cs</button>
                            <button type="button"
                              className={`btn btn-sm ${line.quantityUnit === 'units' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                              onClick={() => setLineQuantityUnit(line.key, 'units')}>un</button>
                          </div>
                        </td>
                        <td className="align-top py-2" style={{ opacity: line.include ? 1 : 0.4 }}>
                          <div className="mb-1" style={{ minHeight: 14 }}>&nbsp;</div>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <input type="number" className="form-control" value={line.unitPriceSnapshot}
                              onChange={e => setLine(line.key, 'unitPriceSnapshot', e.target.value)} />
                          </div>
                        </td>
                        <td className="align-top py-2" style={{ opacity: line.include ? 1 : 0.4 }}>
                          <div className="mb-1" style={{ minHeight: 14 }}>&nbsp;</div>
                          <PriceDiffBadge poPrice={line.extractedUnitPrice} systemPrice={parseFloat(line.unitPriceSnapshot) || null} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mb-3">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addManualLine}>
                    + Add Line Item
                  </button>
                  <span className="text-muted ms-2" style={{ fontSize: 12 }}>In case the AI missed something on the PO.</span>
                </div>

                {meta?.extractedOrderTotal != null && (
                  <div className="small text-muted mb-3">
                    Lines total: <strong>${lineTotalsSum.toFixed(2)}</strong> — PO total: <strong>${Number(meta.extractedOrderTotal).toFixed(2)}</strong>
                    {Math.abs(lineTotalsSum - Number(meta.extractedOrderTotal)) > 0.5 && (
                      <span className="text-warning"> — these don't match, double-check quantities/prices above.</span>
                    )}
                  </div>
                )}
              </>
            )}

            {phase === 'done' && (
              <div className="text-center py-4">
                <div className="mb-2" style={{ fontSize: 32 }}>✅</div>
                <div className="fw-semibold mb-2">
                  Created order {confirmResult?.orderNumber}.
                </div>
                <div className="text-muted small">It's in your order list as a normal pending order — review it like any other.</div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {phase !== 'done' && (
              <button className="btn btn-outline-secondary" onClick={handleClose} disabled={phase === 'confirming'}>Cancel</button>
            )}
            {phase === 'review' || phase === 'confirming' ? (
              <button className="btn btn-primary" onClick={handleConfirm} disabled={phase === 'confirming'}>
                {phase === 'confirming'
                  ? <><span className="spinner-border spinner-border-sm me-2" />Creating…</>
                  : 'Confirm & Create Order'}
              </button>
            ) : phase === 'done' ? (
              <button className="btn btn-primary" onClick={handleClose}>Done</button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
